export type TMonth = {
    month: number;
    year: number;
};

export const compareMonths = (a: TMonth, b: TMonth): number => {
    if (a.year !== b.year) {
        return a.year - b.year;
    }
    return a.month - b.month;
};

const createMonthRange = (
    start: number,
    numMonths: number,
    year: number,
): TMonth[] => {
    const range = [];
    for (let i = start; i <= numMonths + start - 1; i++) {
        range[range.length] = { month: i, year };
    }
    return range;
};

export const getHalfLabelWithMonths = ({
    firstMonthColumn,
    lastMonthColumn,
    year,
    halfString,
    emptyMonths,
}: {
    firstMonthColumn: TMonth | null;
    lastMonthColumn: TMonth | null;
    year: number;
    halfString: string;
    emptyMonths: TMonth[];
}) => {
    const halfStart = halfString === "H1" ? 1 : 7;
    const monthsInHalf = createMonthRange(halfStart, 6, year);
    const monthsInHalfWithoutEmpty = monthsInHalf.filter(
        month =>
            !emptyMonths.some(
                m => m.month === month.month && m.year === month.year,
            ) &&
            firstMonthColumn !== null &&
            compareMonths(month, firstMonthColumn) >= 0 &&
            lastMonthColumn !== null &&
            compareMonths(month, lastMonthColumn) <= 0,
    );
    return monthsInHalfWithoutEmpty.length === 6
        ? halfString
        : `${halfString} (${stringRangeFromMonths(monthsInHalfWithoutEmpty)})`;
};

export const getQuarterLabelWithMonths = ({
    firstMonthColumn,
    lastMonthColumn,
    year,
    quarterString,
    emptyMonths,
}: {
    firstMonthColumn: TMonth | null;
    lastMonthColumn: TMonth | null;
    year: number;
    quarterString: string;
    emptyMonths: TMonth[];
}) => {
    const currentQuarterNumber = Number(quarterString.replace("Q", ""));
    const quarterStart =
        currentQuarterNumber === 1 ? 1 : currentQuarterNumber * 3 - 2;
    const monthsInQuarter = createMonthRange(quarterStart, 3, year);
    const monthsInQuarterWithoutEmpty = monthsInQuarter.filter(
        month =>
            !emptyMonths.some(
                m => m.month === month.month && m.year === month.year,
            ) &&
            firstMonthColumn !== null &&
            compareMonths(month, firstMonthColumn) >= 0 &&
            lastMonthColumn !== null &&
            compareMonths(month, lastMonthColumn) <= 0,
    );
    return monthsInQuarterWithoutEmpty.length === 3
        ? quarterString
        : `${quarterString} (${stringRangeFromMonths(monthsInQuarterWithoutEmpty)})`;
};

export const getYearLabelWithMonths = ({
    firstMonthColumn,
    lastMonthColumn,
    year,
    emptyMonths,
}: {
    firstMonthColumn: TMonth | null;
    lastMonthColumn: TMonth | null;
    year: number;
    emptyMonths: TMonth[];
}) => {
    const yearStart = 1;
    const monthsInYear = createMonthRange(yearStart, 12, year);
    const monthsInYearWithoutEmpty = monthsInYear.filter(
        month =>
            !emptyMonths.some(
                m => m.month === month.month && m.year === month.year,
            ) &&
            firstMonthColumn !== null &&
            compareMonths(month, firstMonthColumn) >= 0 &&
            lastMonthColumn !== null &&
            compareMonths(month, lastMonthColumn) <= 0,
    );

    return monthsInYearWithoutEmpty.length === 12
        ? year.toString()
        : `${year} (${stringRangeFromMonths(monthsInYearWithoutEmpty)})`;
};

export const stringRangeFromMonths = (months: TMonth[]): string => {
    if (months.length === 0) return "No data";

    const monthNumbers = months
        .map(month => month.month)
        .toSorted((a, b) => a - b);

    let result = "";
    let start = monthNumbers[0];
    let end = monthNumbers[0] as number;

    for (const month of monthNumbers.slice(1)) {
        if (month === end + 1) {
            end = month;
        } else {
            result +=
                start === end
                    ? `${start?.toString().padStart(2, "0")},`
                    : `${start?.toString().padStart(2, "0")}-${end?.toString().padStart(2, "0")},`;
            start = month;
            end = month;
        }
    }

    result +=
        start === end
            ? `${start?.toString().padStart(2, "0")}`
            : `${start?.toString().padStart(2, "0")}-${end?.toString().padStart(2, "0")}`;

    return result;
};
